export type EmailRecipientConfig = {
  type: string;
  addresses?: string;
};

export type EmailTemplate = {
  id?: number;
  identifier?: string;
  name?: string;
  companyId?: number | null;
  systemTemplateId?: number | null;
  subject?: string;
  htmlBody?: string;
  textBody?: string | null;
  parameterSchema?: string | null;
  isActive?: boolean;
  scope?: string;
};

export type CompanySystemEmailTemplate = {
  systemTemplateId: number;
  identifier: string;
  name: string;
  subject: string;
  systemIsActive?: boolean;
  companyTemplate?: EmailTemplate | null;
  isEnabledForCompany?: boolean;
};

export type CompanyEmailTabData = {
  systemTemplates?: CompanySystemEmailTemplate[];
  companyTemplates?: EmailTemplate[];
  selectableTemplates?: EmailTemplate[];
  configurations?: Array<{
    companyTestTypeId: number;
    companyTestTypeName: string;
    triggerKind: string;
    triggerLabel: string;
    templateName?: string;
    templateIdentifier?: string;
    isEnabled: boolean;
  }>;
  recentEmails?: Array<{
    id: number;
    to: string;
    subject: string;
    created: string;
    sent?: string | null;
    error?: string | null;
    source?: string | null;
    templateIdentifier?: string | null;
  }>;
};

export type EmailTemplateParameter = {
  key: string;
  source: string;
  label: string;
};

export type CttEmailTrigger = {
  id?: number;
  companyTestTypeId?: number;
  eventType: string;
  emailTemplateId?: number | null;
  isEnabled?: boolean;
  recipientConfig?: EmailRecipientConfig | null;
  parameterOverrides?: Record<string, string> | null;
};

export const CTT_EMAIL_EVENT_TYPES = ["TestCreated", "TestCompleted", "TestGraded"] as const;

export const RECIPIENT_TYPE_OPTIONS = [
  { value: "candidate", label: "Candidate email" },
  { value: "creator", label: "Test creator" },
  { value: "fixed", label: "Fixed addresses" },
];

const PLACEHOLDER_REGEX = /\{([^}]+)\}/g;

export const EMAIL_CONTEXT_DRAG_MIME = "application/x-righttime-email-context";

export type EmailContextSourceOption = {
  source: string;
  label: string;
  group: string;
  dynamicSuffix?: boolean;
  suffixPlaceholder?: string;
};

/** Context variables available when an email is sent (resolved on the server). */
export const EMAIL_CONTEXT_SOURCES: EmailContextSourceOption[] = [
  { source: "test.id", label: "Test ID", group: "Test" },
  { source: "test.firstName", label: "Candidate first name", group: "Test" },
  { source: "test.lastName", label: "Candidate last name", group: "Test" },
  { source: "test.candidateName", label: "Candidate full name", group: "Test" },
  { source: "test.email", label: "Candidate email", group: "Test" },
  { source: "test.companyName", label: "Company name", group: "Test" },
  { source: "test.testTypeName", label: "Test type name", group: "Test" },
  { source: "test.createTime", label: "Test created time", group: "Test" },
  { source: "test.submitTime", label: "Test submitted time", group: "Test" },
  { source: "instance.id", label: "Test instance ID", group: "Test instance" },
  { source: "instance.statusDisplay", label: "Status display name", group: "Test instance" },
  { source: "instance.statusCode", label: "Status code", group: "Test instance" },
  { source: "instance.siteName", label: "Site name", group: "Test instance" },
  { source: "instance.job", label: "Job", group: "Test instance" },
  { source: "instance.location", label: "Location", group: "Test instance" },
  { source: "score.overall", label: "Overall score", group: "Scores" },
  { source: "score.applicant", label: "Applicant score", group: "Scores" },
  { source: "score.company", label: "Company score", group: "Scores" },
  { source: "score.overallMessage", label: "Overall score message", group: "Scores" },
  { source: "score.applicantMessage", label: "Applicant score message", group: "Scores" },
  { source: "score.companyMessage", label: "Company score message", group: "Scores" },
  {
    source: "testField",
    label: "Test field value",
    group: "Test fields",
    dynamicSuffix: true,
    suffixPlaceholder: "Field name (e.g. JobTitle)",
  },
  {
    source: "instanceData",
    label: "Instance data value",
    group: "Instance data",
    dynamicSuffix: true,
    suffixPlaceholder: "Data key (e.g. OverallScore)",
  },
];

const KEY_TO_SOURCE: Record<string, string> = {
  TestId: "test.id",
  FirstName: "test.firstName",
  LastName: "test.lastName",
  CandidateName: "test.candidateName",
  Email: "test.email",
  CompanyName: "test.companyName",
  TestTypeName: "test.testTypeName",
  CreateTime: "test.createTime",
  SubmitTime: "test.submitTime",
  TestInstanceId: "instance.id",
  StatusDisplay: "instance.statusDisplay",
  StatusCode: "instance.statusCode",
  SiteName: "instance.siteName",
  Job: "instance.job",
  Location: "instance.location",
  OverallScore: "score.overall",
  ApplicantScore: "score.applicant",
  CompanyScore: "score.company",
};

export function formatContextPlaceholder(source: string): string {
  const trimmed = source.trim();
  return trimmed ? `{${trimmed}}` : "";
}

export function applyEmailTemplate(text: string, values?: Record<string, unknown> | null): string {
  if (!text || !values) return text;
  return text.replace(PLACEHOLDER_REGEX, (match, key: string) => {
    const token = key.trim();
    if (!token) return match;
    if (Object.prototype.hasOwnProperty.call(values, token)) {
      return String(values[token] ?? "");
    }
    return match;
  });
}

export function extractTemplateParameters(...texts: Array<string | null | undefined>): string[] {
  const keys = new Set<string>();
  for (const text of texts) {
    if (!text) continue;
    for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
      const key = match[1]?.trim();
      if (key) keys.add(key);
    }
  }
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

export function resolvePlaceholderSource(token: string): string {
  if (token.includes(".")) return token;
  return inferSourceFromKey(token) || token;
}

export function buildContextPreviewValues(...texts: Array<string | null | undefined>): Record<string, string> {
  const values: Record<string, string> = {};
  for (const token of extractTemplateParameters(...texts)) {
    const source = resolvePlaceholderSource(token);
    const label = getContextSourceLabel(source) || token;
    values[token] = `«${label}»`;
  }
  return values;
}

export function insertTextAtCaret(
  textarea: HTMLTextAreaElement | null,
  text: string,
  currentValue: string,
  onChange: (next: string) => void
) {
  if (!textarea || !text) {
    onChange(`${currentValue}${text}`);
    return;
  }

  const start = textarea.selectionStart ?? currentValue.length;
  const end = textarea.selectionEnd ?? start;
  const next = currentValue.slice(0, start) + text + currentValue.slice(end);
  onChange(next);

  requestAnimationFrame(() => {
    textarea.focus();
    const pos = start + text.length;
    textarea.setSelectionRange(pos, pos);
  });
}

export function inferSourceFromKey(key: string): string {
  return KEY_TO_SOURCE[key] || "";
}

export function getContextSourceLabel(source?: string | null): string {
  if (!source) return "";
  const parsed = parseSourceSelection(source);
  const option = EMAIL_CONTEXT_SOURCES.find((o) => o.source === parsed.base);
  if (!option) return source;
  if (option.dynamicSuffix && parsed.suffix) {
    return `${option.label}: ${parsed.suffix}`;
  }
  return option.label;
}

export function parseSourceSelection(source: string): { base: string; suffix: string } {
  if (source.startsWith("testField.")) {
    return { base: "testField", suffix: source.slice("testField.".length) };
  }
  if (source.startsWith("instanceData.")) {
    return { base: "instanceData", suffix: source.slice("instanceData.".length) };
  }
  return { base: source, suffix: "" };
}

export function buildSourceValue(base: string, suffix: string): string {
  if (!base) return "";
  const option = EMAIL_CONTEXT_SOURCES.find((o) => o.source === base);
  if (option?.dynamicSuffix) {
    const trimmed = suffix.trim();
    return trimmed ? `${base}.${trimmed}` : "";
  }
  return base;
}

export function buildDefaultEmailTriggers(cttId: number): CttEmailTrigger[] {
  return CTT_EMAIL_EVENT_TYPES.map((eventType) => ({
    companyTestTypeId: cttId,
    eventType,
    isEnabled: false,
    emailTemplateId: null,
    recipientConfig: { type: "candidate" },
    parameterOverrides: {},
  }));
}

export function mergeEmailTriggers(existing: CttEmailTrigger[] | null | undefined, cttId: number): CttEmailTrigger[] {
  const defaults = buildDefaultEmailTriggers(cttId);
  const byEvent = new Map((existing || []).map((t) => [t.eventType, t]));
  return defaults.map((d) => ({ ...d, ...(byEvent.get(d.eventType) || {}) }));
}

export function parseParameterSchema(schema?: string | null): EmailTemplateParameter[] {
  if (!schema) return [];
  try {
    const parsed = JSON.parse(schema);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => {
        const key = String(row?.key || row?.Key || "").trim();
        const source = String(row?.source || row?.Source || inferSourceFromKey(key) || "").trim();
        const label = String(row?.label || row?.Label || getContextSourceLabel(source) || key).trim();
        return { key, source, label };
      })
      .filter((row) => row.key);
  } catch {
    return [];
  }
}

export function mergeDetectedParameters(
  keys: string[],
  existing: EmailTemplateParameter[]
): EmailTemplateParameter[] {
  const byKey = new Map(existing.map((p) => [p.key.toLowerCase(), p]));
  return keys.map((key) => {
    const prev = byKey.get(key.toLowerCase());
    const source = prev?.source || inferSourceFromKey(key);
    return {
      key,
      source,
      label: prev?.label || getContextSourceLabel(source) || key,
    };
  });
}

export function serializeParameterSchema(rows: EmailTemplateParameter[]): string {
  return JSON.stringify(
    rows
      .filter((r) => r.key.trim())
      .map((r) => ({
        key: r.key.trim(),
        source: r.source.trim(),
        label: r.label.trim() || getContextSourceLabel(r.source) || r.key.trim(),
      }))
  );
}
